import { useState, useEffect } from "react";
import VideoRecorder from "../video-recorder";
import AudioRecorder from "../audio-recorder";
import Textarea from "@/components/ui/textarea";
import BoldLayout from "../bold-layout";
import { useFeedbackFlow } from "../../hooks/use-feedback-flow";
import type {
	ApiPage,
	ApiQuestion,
	Brandkit,
	FormBrand,
} from "../../types/api.types";

interface StepQuestionProps {
	brandkit: Brandkit;
	brand: FormBrand;
	showBackButton?: boolean;
	onBack?: () => void;
	stepKey?: string;
	pageConfig: ApiPage;
	question: ApiQuestion;
	questionIndex: number;
	totalQuestions: number;
	totalSteps: number;
	currentStepIndex: number;
	isDarkMode?: boolean;
}

export default function StepQuestion({
	brandkit,
	brand,
	showBackButton,
	onBack,
	stepKey,
	pageConfig: _pageConfig,
	question,
	questionIndex,
	totalQuestions,
	totalSteps,
	currentStepIndex,
	isDarkMode,
}: StepQuestionProps) {
	// _pageConfig available for future use
	void _pageConfig;
	const {
		data,
		setQuestionAnswer,
		nextStep,
		canProceed,
		updateQuestionAnswerStep,
		isSubmitting,
		submitError,
	} = useFeedbackFlow();
	const [error, setError] = useState<string | null>(null);

	const answer = data.questionAnswers[question.id] || {
		questionId: question.id,
		type: "text" as const,
		textContent: "",
		videoFile: null,
		videoPreview: null,
		audioFile: null,
		audioPreview: null,
	};

	const [selectedType, setSelectedType] = useState<
		"text" | "video" | "audio" | null
	>(null);

	useEffect(() => {
		const currentAnswer = data.questionAnswers[question.id];
		// If there's already a type set in the answer, use it
		if (currentAnswer?.type) {
			setSelectedType(currentAnswer.type);
		} else if (question.format === "text") {
			setSelectedType("text");
		} else if (question.format === "video") {
			setSelectedType("video");
		} else if (question.format === "audio") {
			setSelectedType("audio");
		} else {
			setSelectedType(null);
		}
	}, [question.id, question.format]);

	const showFormatChoice = question.format === "any";
	const forcedFormat =
		question.format === "text"
			? "text"
			: question.format === "video"
				? "video"
				: question.format === "audio"
					? "audio"
					: null;
	const effectiveType = forcedFormat || selectedType;

	const handleSelectType = (type: "text" | "video" | "audio") => {
		setSelectedType(type);
		setQuestionAnswer(question.id, { type });
	};

	const handleTextChange = (text: string) => {
		setQuestionAnswer(question.id, { textContent: text, type: "text" });
	};

	const handleVideoComplete = (file: File, previewUrl: string) => {
		setQuestionAnswer(question.id, {
			videoFile: file,
			videoPreview: previewUrl,
			type: "video",
		});
	};

	const handleVideoReset = () => {
		setQuestionAnswer(question.id, {
			videoFile: null,
			videoPreview: null,
			videoUrl: undefined,
		});
	};

	const handleAudioComplete = (file: File, previewUrl: string, audioDuration?: number) => {
		setQuestionAnswer(question.id, {
			audioFile: file,
			audioPreview: previewUrl,
			audioDuration,
			type: "audio",
		});
	};

	const handleAudioReset = () => {
		setQuestionAnswer(question.id, {
			audioFile: null,
			audioPreview: null,
			audioUrl: undefined,
		});
	};

	const handleContinue = async () => {
		if (!canProceed()) return;

		try {
			setError(null);
			// Upload files and submit answer incrementally
			await updateQuestionAnswerStep(question.id);
			nextStep();
		} catch (err) {
			setError(
				submitError || "Failed to save your answer. Please try again.",
			);
		}
	};

	const canContinue = () => {
		return canProceed();
	};

	// Calculate progress - questions come after welcome page
	const progressCurrent = currentStepIndex + 1;
	const progressTotal = totalSteps;

	const brandLogo =
		brandkit?.logo_icon_with_label_url ||
		brandkit?.logo_icon_only_url ||
		brandkit?.logo_label_only_url ||
		brandkit?.logo_inverted_icon_with_label_url ||
		brandkit?.logo_inverted_icon_only_url ||
		brandkit?.logo_inverted_label_only_url ||
		null;

	return (
		<BoldLayout
			showBranding={brand?.branding !== "disabled"}
			brandName={brand.name}
			brandLogo={brandLogo}
			brandkit={brandkit}
			stepKey={stepKey}
			showBackButton={showBackButton}
			onBack={onBack}
			eyebrow={
				totalQuestions > 1
					? `Question ${questionIndex + 1} of ${totalQuestions}`
					: undefined
			}
			title={question.title}
			description={question.description}
			progress={{ current: progressCurrent, total: progressTotal }}
			onContinue={handleContinue}
			continueDisabled={!canContinue() || isSubmitting}
			continueLabel={isSubmitting ? "Saving..." : undefined}
			centered={false}
			isDarkMode={isDarkMode}
		>
			{showFormatChoice && effectiveType === null ? (
				<div className="space-y-3">
					<button
						type="button"
						onClick={() => handleSelectType("video")}
						className={`w-full p-5 border-2 rounded-xl shadow-sm transition-all text-left group cursor-pointer flex items-start gap-4 relative ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
						style={{
							borderColor:
								brandkit?.color_accent_hex || "#6366f1",
						}}
					>
						<span
							className="absolute top-0 right-4 -translate-y-1/2 px-2 py-0.5 text-xs font-medium rounded-full text-white flex items-center gap-1"
							style={{
								backgroundColor:
									brandkit?.color_accent_hex || "#6366f1",
								fontFamily: "var(--font-body)",
							}}
						>
							<svg
								className="w-3 h-3"
								fill="currentColor"
								viewBox="0 0 20 20"
							>
								<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
							</svg>
							Recommended
						</span>
						<svg
							className={`w-6 h-6 mt-0.5 shrink-0 transition-colors ${isDarkMode ? "text-gray-400 group-hover:text-gray-200" : "text-gray-400 group-hover:text-gray-600"}`}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={1.5}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
							/>
						</svg>
						<div className="flex-1">
							<p
								className={`font-medium ${isDarkMode ? "text-white" : "text-t-primary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								{question?.video_option_label ||
									"Record a video testimonial"}
							</p>
							<p
								className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								{question.video_option_description ||
									"Share your story face-to-face. Video testimonials feel more personal and help others connect with your experience."}
							</p>
						</div>
					</button>

					<button
						type="button"
						onClick={() => handleSelectType("audio")}
						className={`w-full p-5 border rounded-xl shadow-sm hover:border-gray-300 transition-all text-left group cursor-pointer flex items-start gap-4 ${isDarkMode ? "border-gray-700 bg-gray-800 hover:border-gray-600" : "border-gray-100 bg-white"}`}
					>
						<svg
							className={`w-6 h-6 mt-0.5 shrink-0 transition-colors ${isDarkMode ? "text-gray-400 group-hover:text-gray-200" : "text-gray-400 group-hover:text-gray-600"}`}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={1.5}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
							/>
						</svg>
						<div className="flex-1">
							<p
								className={`font-medium ${isDarkMode ? "text-white" : "text-t-primary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								{question?.audio_option_label ||
									"Record an audio testimonial"}
							</p>
							<p
								className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								{question?.audio_option_description ||
									"Prefer voice? Record your thoughts as audio. Quick and easy to share your experience."}
							</p>
						</div>
					</button>

					<button
						type="button"
						onClick={() => handleSelectType("text")}
						className={`w-full p-5 border rounded-xl shadow-sm hover:border-gray-300 transition-all text-left group cursor-pointer flex items-start gap-4 ${isDarkMode ? "border-gray-700 bg-gray-800 hover:border-gray-600" : "border-gray-100 bg-white"}`}
					>
						<svg
							className={`w-6 h-6 mt-0.5 shrink-0 transition-colors ${isDarkMode ? "text-gray-400 group-hover:text-gray-200" : "text-gray-400 group-hover:text-gray-600"}`}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={1.5}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
							/>
						</svg>
						<div className="flex-1">
							<p
								className={`font-medium ${isDarkMode ? "text-white" : "text-t-primary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								{question?.text_option_label ||
									"Write a text testimonial"}
							</p>
							<p
								className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-secondary"}`}
								style={{
									fontFamily: "var(--font-body)",
									fontWeight: "var(--font-body-weight)",
								}}
							>
								{question?.text_option_description ||
									"Prefer typing? Write out your thoughts at your own pace. You can always edit before submitting."}
							</p>
						</div>
					</button>
				</div>
			) : (
				<div className="space-y-4">
					{showFormatChoice && (
						<button
							type="button"
							onClick={() => {
								// Set selectedType to null first to show format selection
								setSelectedType(null);
								// Clear existing answer when switching
								setQuestionAnswer(question.id, {
									textContent: "",
									videoFile: null,
									videoPreview: null,
									videoUrl: undefined,
									audioFile: null,
									audioPreview: null,
									audioUrl: undefined,
								});
							}}
							className={`text-sm flex items-center gap-1.5 transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-secondary hover:text-t-primary"}`}
							style={{
								fontFamily: "var(--font-body)",
								fontWeight: "var(--font-body-weight)",
							}}
						>
							<svg
								className="w-4 h-4"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								strokeWidth={2}
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
							Change format
						</button>
					)}

					{effectiveType === "text" ? (
						<Textarea
							value={answer.textContent}
							onChange={(e) => handleTextChange(e.target.value)}
							placeholder="Share your experience..."
							rows={6}
							isDark={isDarkMode}
						/>
					) : effectiveType === "audio" ? (
						<AudioRecorder
							onRecordingComplete={handleAudioComplete}
							existingAudioFile={answer.audioFile}
							existingAudioUrl={answer.audioUrl}
							onReset={handleAudioReset}
							isDarkMode={isDarkMode}
						/>
					) : (
						<VideoRecorder
							onRecordingComplete={handleVideoComplete}
							existingVideoFile={answer.videoFile}
							existingVideoUrl={answer.videoUrl}
							onReset={handleVideoReset}
							fullHeight
						/>
					)}
				</div>
			)}

			{error && (
				<div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
					<p className="text-sm text-red-600">{error}</p>
					<button
						onClick={handleContinue}
						className="mt-2 text-sm text-red-700 underline hover:no-underline"
					>
						Try Again
					</button>
				</div>
			)}
		</BoldLayout>
	);
}
